// ============================================================================
// Document version control — drafts, publishing, supersession (brief A §3a)
// ============================================================================
// fusion_documents is a row-per-version table: each row carries its own
// content, version_value, status and parent_document_id. This module owns the
// transitions between those rows so no page has to remember the ordering.
//
// The rule that drives all of it (§3a): EDITING A LIVE DOCUMENT CREATES A
// DRAFT. The live version stays live — and stays signable — until the draft is
// published. Before this, Save-draft wrote straight onto the live row, which
// silently rewrote content suppliers had already signed. The DB now rejects
// that (fusion_documents_published_content_immutable), so the draft row is the
// only place an edit can go.
//
// Ordering matters when publishing: only ONE row per compliance_doc_key may be
// status='active', so the live row must be superseded BEFORE the draft is
// promoted. Every step here rolls back if the next one fails.
// ============================================================================

import { supabase } from '../supabaseClient';
import { contentHashForDocument, RESIGN_POLICIES } from './documentVersion';
import { sendEmail, complianceResignRequiredEmail } from './email';
import { formatDateBritish } from './compliance';

// Columns that belong to a VERSION rather than to the document's configuration.
// A new draft copies the config verbatim and resets these.
const VERSION_COLUMNS = {
  content_hash: null,
  published_at: null,
  published_by_user_id: null,
  // Approval mode resets on every version (document-versioning §3a): new
  // content must be read by a person at least once before it is trusted to
  // approve itself. The DB enforces this too — this keeps the draft row honest
  // from the moment it's created, so the editor never shows "auto" for content
  // nobody has reviewed.
  approval_mode: 'manual',
  resign_policy: RESIGN_POLICIES.NONE,
  resign_deadline: null,
  change_reason: null,
  material: false,
  superseded_at: null,
  superseded_by_id: null,
};

// Which row should the editor open for `docId`?
//   - a draft row            → edit it (and report the live version it's a draft OF)
//   - a live row with a draft→ edit the draft; the live row keeps serving suppliers
//   - a live row, no draft   → edit its content in memory; the draft row is
//                              created on the first save (`row` === `live`)
// Returns { row, live, error }. `live` is null when nothing is published yet.
export async function resolveEditableVersion(docId) {
  const { data: row, error } = await supabase
    .from('fusion_documents').select('*').eq('id', docId).single();
  if (error || !row) return { error: error?.message || 'Document not found.' };

  if (row.status === 'active') {
    const { data: drafts } = await supabase
      .from('fusion_documents').select('*')
      .eq('parent_document_id', row.id)
      .eq('status', 'draft')
      .order('created_at', { ascending: false })
      .limit(1);
    const draft = (drafts || [])[0];
    return { row: draft || row, live: row };
  }

  // A draft: find the live version it descends from, if any.
  let live = null;
  if (row.parent_document_id) {
    const { data } = await supabase
      .from('fusion_documents').select('*')
      .eq('id', row.parent_document_id).maybeSingle();
    live = data && data.status === 'active' ? data : null;
  }
  return { row, live };
}

// `content` is whichever columns hold this document's content — { form_schema }
// for a hosted form, { field_layout } for a pdf_sign document. Both editors go
// through the same draft/publish machinery; only the columns differ.

// Start a draft of a live document. Config is copied verbatim (settings never
// version); content is the caller's working copy; version_value is the live
// one + 1 so the draft already knows what it will publish as.
export async function createDraftFrom(live, content) {
  const { id, created_at, updated_at, ...config } = live; // eslint-disable-line no-unused-vars
  const draftRow = {
    ...config,
    ...VERSION_COLUMNS,
    ...content,
    status: 'draft',
    is_active: false,
    version_value: (Number(live.version_value) || 1) + 1,
    parent_document_id: live.id,
  };
  const { data, error } = await supabase
    .from('fusion_documents').insert([draftRow]).select('*').single();
  if (error) throw new Error(error.message);
  return data;
}

// Save working content. `row` must be a draft — the DB refuses anything else,
// and this is the guard that turns that into a sentence a human can act on.
export async function saveDraftContent(row, content) {
  if (row.status !== 'draft') {
    throw new Error('This version is published — edits have to go into a draft.');
  }
  const { error } = await supabase
    .from('fusion_documents').update(content).eq('id', row.id);
  if (error) throw new Error(error.message);
}

// Throw the draft away. Safe by definition: nothing can be signed against an
// unpublished version.
export async function discardDraft(draftId) {
  const { error } = await supabase
    .from('fusion_documents').delete().eq('id', draftId).eq('status', 'draft');
  if (error) throw new Error(error.message);
}

// Which suppliers signed a given version. The publish dialog states the count
// back ("this takes N suppliers out of play today"); the notification pass
// needs the ids themselves. Counted across both signature tables by
// document_key + version number rather than by the FK, so signatures taken
// before version_document_id existed still count.
export async function suppliersOnVersion(doc) {
  const key = doc?.compliance_doc_key;
  const version = doc?.version_value;
  if (!key || !version) return [];

  const [acks, subs] = await Promise.all([
    supabase.from('document_acknowledgements')
      .select('supplier_id, review_decision')
      .eq('document_key', key).eq('document_version', version),
    supabase.from('hosted_form_submissions')
      .select('supplier_id')
      .eq('document_key', key).eq('document_version', version),
  ]);

  const suppliers = new Set();
  for (const a of acks.data || []) {
    // A rejected acknowledgement isn't a live signature — they have to sign
    // again anyway, so a new version doesn't change anything for them.
    if (a.review_decision === 'rejected') continue;
    if (a.supplier_id) suppliers.add(a.supplier_id);
  }
  for (const s of subs.data || []) if (s.supplier_id) suppliers.add(s.supplier_id);
  return [...suppliers];
}

export async function countSuppliersOnVersion(doc) {
  return (await suppliersOnVersion(doc)).length;
}

// Tell the affected suppliers, at publish time, that they have to sign again
// (brief A §7). Only B and C generate a message — A changed nothing for them,
// and an email that says "nothing has changed, no action needed" is noise.
//
// Best-effort by design: the version is already published and correct: a
// SendGrid failure must not roll that back. Returns a summary the caller can
// surface so a silent failure is still visible.
export async function notifyResignRequired({ supersededVersion, newVersion, choice }) {
  const policy = choice?.resignPolicy || RESIGN_POLICIES.NONE;
  if (policy === RESIGN_POLICIES.NONE) return { sent: 0, failed: 0, suppliers: 0 };

  const supplierIds = await suppliersOnVersion(supersededVersion);
  if (supplierIds.length === 0) return { sent: 0, failed: 0, suppliers: 0 };

  const { data: suppliers } = await supabase
    .from('suppliers')
    .select('id, supplier_name, main_contact_name, main_contact_email, secondary_contact_name, secondary_contact_email, is_record_only, status')
    .in('id', supplierIds);

  const portalUrl = `${window.location.origin}/portal/compliance`;
  const deadlineLabel = choice?.resignDeadline ? formatDateBritish(choice.resignDeadline) : null;
  let sent = 0;
  let failed = 0;

  for (const supplier of suppliers || []) {
    // Record-only and deactivated suppliers aren't being asked for anything.
    if (supplier.is_record_only || supplier.status === 'inactive') continue;

    const recipients = [];
    if (supplier.main_contact_email) recipients.push({ email: supplier.main_contact_email, name: supplier.main_contact_name });
    if (supplier.secondary_contact_email && supplier.secondary_contact_email !== supplier.main_contact_email) {
      recipients.push({ email: supplier.secondary_contact_email, name: supplier.secondary_contact_name });
    }
    if (recipients.length === 0) { failed++; continue; }

    for (const r of recipients) {
      const { subject, html } = complianceResignRequiredEmail({
        contactName: r.name,
        supplierName: supplier.supplier_name,
        documentLabel: newVersion?.document_name || 'A compliance document',
        changeReason: choice?.changeReason || null,
        deadline: deadlineLabel,
        immediate: policy === RESIGN_POLICIES.IMMEDIATE,
        portalUrl,
      });
      try {
        await sendEmail({ to: r.email, subject, html });
        sent++;
      } catch {
        failed++;
      }
    }

    // Ledger row so the cron's 14/7/1 reminders can tell this apart from a
    // document-expiry reminder, and so the send is visible in the audit trail.
    // 'reminder_sent' is an existing valid action; meta.type discriminates.
    await supabase.from('compliance_audit_log').insert([{
      supplier_id: supplier.id,
      document_key: newVersion?.compliance_doc_key || supersededVersion?.compliance_doc_key || null,
      action: 'reminder_sent',
      meta: {
        type: 'resign_required',
        version_id: newVersion?.id || null,
        version_value: newVersion?.version_value || null,
        resign_policy: policy,
        resign_deadline: choice?.resignDeadline || null,
      },
    }]).then(() => {}, () => {});
  }

  return { sent, failed, suppliers: (suppliers || []).length };
}

// Publish `draft` as the new live version.
//
// `choice` answers the question every publish has to answer (§1): what happens
// to suppliers who signed the previous version — nothing (A), sign again by a
// deadline (B), or sign again now (C). It is stored on the VERSION, so the
// answer stays attached to the change that caused it.
//
// A first publish (nothing live yet) takes no choice: there is no previous
// version and nobody to re-sign.
export async function publishVersion({ draft, live, content, publishedByUserId, choice, pdfBytes }) {
  const contentHash = contentHashForDocument({ ...draft, ...content }, { pdfBytes });
  const nowIso = new Date().toISOString();

  const publishPatch = {
    ...content,
    status: 'active',
    is_active: true,
    superseded_at: null,
    superseded_by_id: null,
    content_hash: contentHash,
    published_at: nowIso,
    published_by_user_id: publishedByUserId || null,
  };

  // First publish — flip the draft live in place. No previous version exists.
  if (!live || live.id === draft.id) {
    const { error } = await supabase.from('fusion_documents')
      .update({ ...publishPatch, resign_policy: RESIGN_POLICIES.NONE, resign_deadline: null, change_reason: null, material: false })
      .eq('id', draft.id);
    if (error) throw new Error(error.message);
    return { id: draft.id };
  }

  const policy = choice?.resignPolicy || RESIGN_POLICIES.NONE;
  const versionPatch = {
    ...publishPatch,
    resign_policy: policy,
    resign_deadline: policy === RESIGN_POLICIES.DEADLINE ? (choice?.resignDeadline || null) : null,
    change_reason: policy === RESIGN_POLICIES.NONE ? null : (choice?.changeReason || '').trim() || null,
    material: policy === RESIGN_POLICIES.NONE ? false : true,
  };

  // 1. Supersede the live row FIRST — only one active row per key is allowed.
  const { error: supErr } = await supabase.from('fusion_documents')
    .update({ status: 'superseded', is_active: false, superseded_at: nowIso })
    .eq('id', live.id);
  if (supErr) throw new Error(supErr.message);

  // 2. Promote the draft.
  const { error: pubErr } = await supabase.from('fusion_documents')
    .update(versionPatch).eq('id', draft.id);
  if (pubErr) {
    // Put the live version back — never leave the document with no live row.
    await supabase.from('fusion_documents')
      .update({ status: 'active', is_active: true, superseded_at: null })
      .eq('id', live.id);
    throw new Error(pubErr.message);
  }

  // 3. Link the old version forward. Best-effort: the version chain is already
  // walkable via parent_document_id, so a failure here doesn't undo a good
  // publish.
  await supabase.from('fusion_documents')
    .update({ superseded_by_id: draft.id }).eq('id', live.id);

  // 4. Tell the suppliers who signed the old version (§7). After the publish,
  // never before it — the version being live is what the email is about, and a
  // mail failure must not leave the document unpublished.
  const notified = await notifyResignRequired({
    supersededVersion: live,
    newVersion: { ...draft, ...content, ...versionPatch },
    choice,
  }).catch(() => ({ sent: 0, failed: 0, suppliers: 0, error: true }));

  return { id: draft.id, notified };
}

// Chase the suppliers who still owe a signature on a live version (brief B §3).
// Same email as the publish-time one, sent on demand from the version history.
//
// Rate-limited to once per supplier per version per 7 days, matching the
// compliance chase rule — a chase that can be fired repeatedly stops being a
// chase and becomes spam. Returns { sent, failed, skipped } so the caller can
// say exactly what happened, including "nothing, they were chased on Tuesday".
export async function chaseResignOutstanding(version, supplierIds) {
  const ids = [...new Set((supplierIds || []).filter(Boolean))];
  if (!version || ids.length === 0) return { sent: 0, failed: 0, skipped: 0 };

  const { data: suppliers } = await supabase
    .from('suppliers')
    .select('id, supplier_name, main_contact_name, main_contact_email, secondary_contact_name, secondary_contact_email, is_record_only, status')
    .in('id', ids);

  const portalUrl = `${window.location.origin}/portal/compliance`;
  const deadlineLabel = version.resign_deadline ? formatDateBritish(version.resign_deadline) : null;
  const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
  let sent = 0;
  let failed = 0;
  let skipped = 0;

  for (const supplier of suppliers || []) {
    if (supplier.is_record_only || supplier.status === 'inactive') { skipped++; continue; }

    const { data: recent } = await supabase
      .from('compliance_audit_log')
      .select('id')
      .eq('supplier_id', supplier.id)
      .eq('document_key', version.compliance_doc_key)
      .eq('action', 'reminder_sent')
      .eq('meta->>type', 'resign_chase')
      .eq('meta->>version_id', String(version.id))
      .gte('ts', sevenDaysAgo)
      .limit(1);
    if (recent && recent.length > 0) { skipped++; continue; }

    const recipients = [];
    if (supplier.main_contact_email) recipients.push({ email: supplier.main_contact_email, name: supplier.main_contact_name });
    if (supplier.secondary_contact_email && supplier.secondary_contact_email !== supplier.main_contact_email) {
      recipients.push({ email: supplier.secondary_contact_email, name: supplier.secondary_contact_name });
    }
    if (recipients.length === 0) { failed++; continue; }

    let allSent = true;
    for (const r of recipients) {
      const { subject, html } = complianceResignRequiredEmail({
        contactName: r.name,
        supplierName: supplier.supplier_name,
        documentLabel: version.document_name || version.compliance_doc_key,
        changeReason: version.change_reason || null,
        deadline: deadlineLabel,
        immediate: version.resign_policy === RESIGN_POLICIES.IMMEDIATE,
        portalUrl,
      });
      try {
        await sendEmail({ to: r.email, subject, html });
      } catch {
        allSent = false;
      }
    }

    if (allSent) {
      sent++;
      await supabase.from('compliance_audit_log').insert([{
        supplier_id: supplier.id,
        document_key: version.compliance_doc_key,
        action: 'reminder_sent',
        meta: { type: 'resign_chase', version_id: version.id, version_value: version.version_value },
      }]).then(() => {}, () => {});
    } else {
      failed++;
    }
  }

  return { sent, failed, skipped };
}

// Pick the version a label/metadata lookup should use when it has only a
// compliance_doc_key. Live version wins; otherwise the highest version number.
// Drafts are never the answer — they aren't published to anyone.
export function pickLiveVersion(rows) {
  const usable = (rows || []).filter(r => r && r.status !== 'draft');
  if (usable.length === 0) return null;
  return usable.find(r => r.status === 'active')
    || usable.slice().sort((a, b) => (Number(b.version_value) || 0) - (Number(a.version_value) || 0))[0];
}
